import type { SiteCmsHtmlMark, SiteCmsHtmlNode } from "../types/entities";

/**
 * "HTML / Word" section bounds. The site renderer keeps an identical,
 * dependency-free copy (packages/site-renderer/src/html-doc.mjs); a parity
 * test asserts both stay equal.
 */
export const CMS_HTML_LIMITS = Object.freeze({
  /** Source characters accepted by the conversion endpoint (256 KiB). */
  maxSourceChars: 262_144,
  maxCharacters: 10_000,
  maxNodes: 1_500,
  maxDepth: 10,
  maxRows: 40,
  maxColumns: 10,
  maxSerialized: 60_000,
  maxStart: 9_999,
});

export function emptyCmsHtmlDoc(): SiteCmsHtmlNode {
  return { type: "doc", content: [{ type: "paragraph" }] };
}

/** Every link destination of the document, in document order. */
export function cmsHtmlDocLinks(doc: SiteCmsHtmlNode): string[] {
  const result: string[] = [];
  const visit = (node: SiteCmsHtmlNode): void => {
    for (const mark of node.marks ?? [])
      if (mark.type === "link") result.push(mark.attrs.href);
    for (const child of node.content ?? []) visit(child);
  };
  visit(doc);
  return result;
}

/** Rewrite link destinations (page rename, duplication); structure is kept. */
export function mapCmsHtmlDocLinks(
  doc: SiteCmsHtmlNode,
  rewrite: (href: string) => string,
): SiteCmsHtmlNode {
  return {
    ...doc,
    ...(doc.marks
      ? {
          marks: doc.marks.map((mark) =>
            mark.type === "link"
              ? { type: "link", attrs: { href: rewrite(mark.attrs.href) } }
              : mark,
          ),
        }
      : {}),
    ...(doc.content
      ? {
          content: doc.content.map((child) =>
            mapCmsHtmlDocLinks(child, rewrite),
          ),
        }
      : {}),
  };
}

/** Visible text length (hard breaks excluded), as counted by every validator. */
export function cmsHtmlDocCharacters(doc: SiteCmsHtmlNode): number {
  let total = doc.type === "text" ? (doc.text ?? "").length : 0;
  for (const child of doc.content ?? []) total += cmsHtmlDocCharacters(child);
  return total;
}

function record(value: unknown): Record<string, unknown> | null {
  return value && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

/**
 * Project Tiptap's getJSON() output onto the canonical stored contract:
 * drops editor defaults (link target/rel/class, orderedList type, start 1)
 * and empty arrays. It never invents content and is NOT a security barrier:
 * unknown types are passed through so that API/renderer validation rejects them.
 */
export function normalizeCmsHtmlDoc(value: unknown): SiteCmsHtmlNode {
  const node = record(value);
  if (!node || typeof node.type !== "string")
    return { type: "doc", content: [] };
  const result: SiteCmsHtmlNode = {
    type: node.type as SiteCmsHtmlNode["type"],
  };
  const attrs = record(node.attrs);
  if (node.type === "heading")
    result.attrs = { level: attrs?.level === 3 ? 3 : 2 };
  if (
    node.type === "orderedList" &&
    typeof attrs?.start === "number" &&
    attrs.start !== 1
  )
    result.attrs = { start: attrs.start };
  if (node.type === "text") result.text = String(node.text ?? "");
  if (Array.isArray(node.marks) && node.marks.length) {
    // Canonical order (bold, italic, link): Tiptap orders marks by priority.
    const rank = (mark: SiteCmsHtmlMark) =>
      ["bold", "italic", "link"].indexOf(mark.type) >>> 0;
    result.marks = node.marks.map((raw): SiteCmsHtmlMark => {
      const mark = record(raw);
      if (mark?.type === "link")
        return {
          type: "link",
          attrs: { href: String(record(mark.attrs)?.href ?? "") },
        };
      return { type: mark?.type } as SiteCmsHtmlMark;
    });
    result.marks.sort((a, b) => rank(a) - rank(b));
  }
  if (Array.isArray(node.content) && node.content.length)
    result.content = node.content.map(normalizeCmsHtmlDoc);
  return result;
}
